/**
 * OPO — Cornelius memory-eval runs (Tier-3 A5).
 */
import React, { useState, useEffect, useCallback } from 'react';

const card = {
  background: 'var(--card-bg)',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--border-radius)',
  padding: '12px 14px',
  marginBottom: 10,
  color: 'var(--text-primary)',
};

function pct(n, d) {
  if (!d) return '—';
  return `${Math.round((n / d) * 100)}%`;
}

function PassBar({ run }) {
  const total = run.total || 0;
  const seg = (n, color) => ({
    flex: total ? n / total : 0,
    background: color,
    height: 6,
    minWidth: n > 0 ? 4 : 0,
  });
  return (
    <div style={{ display: 'flex', borderRadius: 4, overflow: 'hidden', marginTop: 6 }}>
      <div style={seg(run.passed, 'var(--success-color)')} />
      <div style={seg(run.abstained, 'darkorange')} />
      <div style={seg(run.failed, 'var(--danger-color)')} />
    </div>
  );
}

export default function AdminCorneliusEval() {
  const [runs, setRuns] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch('/api/opo/cornelius-memories/eval/runs');
      const d = await r.json();
      if (!r.ok || d.success === false) throw new Error(d.error || `HTTP ${r.status}`);
      setRuns(d.runs || []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function open(id) {
    setActive({ id, loading: true });
    try {
      const r = await fetch(`/api/opo/cornelius-memories/eval/runs/${id}`);
      const d = await r.json();
      if (!r.ok || d.success === false) throw new Error(d.error || `HTTP ${r.status}`);
      setActive({ id, run: d.run });
    } catch (e) {
      setActive({ id, error: e.message });
    }
  }

  const results = active?.run?.results;
  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ marginBottom: 14, color: 'var(--text-secondary)', fontSize: 13 }}>
        Memory-eval runs (<code>gfa_eval_runs</code>) populated by <code>scripts/memeval_runner.py</code>.
      </div>

      <button onClick={load} style={{
        padding: '7px 16px',
        borderRadius: 'var(--border-radius)',
        border: 'none',
        background: 'var(--primary-color)',
        color: 'var(--text-on-primary)',
        fontWeight: 600,
        cursor: 'pointer',
        marginBottom: 14,
      }}>Refresh</button>

      {error && <div style={{ color: 'var(--danger-color)', marginBottom: 12, fontSize: 13 }}>{error}</div>}
      {loading && <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>}
      {!loading && runs.length === 0 && (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>No eval runs found.</div>
      )}

      {runs.map((run) => (
        <div key={run.id} style={card}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong>#{run.id}</strong>
            <code style={{ fontSize: 11 }}>{run.suite_name}</code>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
              total {run.total} • passed {run.passed} ({pct(run.passed, run.total)}) • abstained {run.abstained} • failed {run.failed}
            </span>
            <button onClick={() => open(run.id)} style={{
              marginLeft: 'auto',
              padding: '4px 10px',
              fontSize: 11,
              borderRadius: 6,
              border: '1px solid var(--border-color)',
              background: 'transparent',
              color: 'var(--text-primary)',
              cursor: 'pointer',
            }}>{active && active.id === run.id ? 'Hide' : 'View'}</button>
          </div>
          <PassBar run={run} />
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
            started {run.started_at ? new Date(run.started_at).toLocaleString() : '—'} •
            {' '}finished {run.finished_at ? new Date(run.finished_at).toLocaleString() : '—'} •
            {' '}host {run.hostname || '—'}
          </div>

          {active && active.id === run.id && (
            <pre style={{
              marginTop: 10,
              maxHeight: 260,
              overflow: 'auto',
              background: 'var(--surface-color)',
              border: '1px solid var(--border-color)',
              borderRadius: 6,
              padding: 8,
              fontSize: 11,
              color: 'var(--text-primary)',
            }}>
              {active.error
                ? `Error: ${active.error}`
                : JSON.stringify(active.run?.results || results || {}, null, 2)}
            </pre>
          )}
        </div>
      ))}
    </div>
  );
}
